import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import CompanyTable from '@/features/company/components/CompanyTable'
import { useCompanyList } from '@/features/company/api/queries'
import { isPaywall } from '@/api/errors'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { companiesSearchSchema, loadCompanies } from '@/screens/app/CompaniesScreen.loader'

/**
 * The page's frame: `ScreenLayout fullWidth` (no rail, as before — a 360px
 * rail would leave the table unreadable) with `TitleRow` for the header, so
 * the pending, error and loaded pages are the same page region to the page
 * transition. No `mainClassName` override: the gap above the table is
 * ScreenLayout's own `gap-6`.
 */
const FRAME = { fullWidth: true } as const

const HEADER_SKELETON = (
  <TitleRow
    title="Companies"
    meta={<Skeleton className="h-3 w-20 rounded-xs" />}
    actions={<Skeleton className="h-9 w-64 rounded-md" />}
  />
)

/**
 * The table as it loads for a real register: the 40px header over rows that
 * run past DataTable's `32rem` scroll cap, so the box is 512px plus its
 * hairline border whatever the count. A search that narrows it to a handful
 * is the one case where the loaded table is shorter.
 */
function CompaniesPending() {
  return (
    <ScreenLayout {...FRAME} pending pendingLabel="Loading companies…" header={HEADER_SKELETON}>
      <div className="overflow-hidden rounded-lg border border-border-subtle bg-card">
        <div className="h-128 overflow-hidden">
          <div className="flex h-10 items-center gap-6 border-b border-border-subtle px-3">
            <Skeleton className="h-3 w-14 rounded-xs" />
            <Skeleton className="h-3 w-20 rounded-xs" />
            <Skeleton className="ml-auto h-3 w-24 rounded-xs" />
          </div>
          {Array.from({ length: 11 }, (_, index) => (
            <div key={index} className="flex h-11 items-center border-b border-border-subtle px-3">
              <Skeleton className="h-4 w-full rounded-xs" />
            </div>
          ))}
        </div>
      </div>
    </ScreenLayout>
  )
}

/**
 * A paywalled register is not a failure to retry — "Try again" would get the
 * same answer forever — so it keeps the words the screen has always given it.
 * Anything else is the route's error page, in the same frame.
 */
function CompaniesError(props: ErrorComponentProps) {
  if (isPaywall(props.error)) {
    return (
      <ScreenLayout {...FRAME}>
        <EmptyState
          title="Subscription required"
          description="This data needs an active Guru plan."
        />
      </ScreenLayout>
    )
  }
  return <RouteErrorPage {...props} header={HEADER_SKELETON} title="Companies did not load" />
}

export const Route = createFileRoute('/_app/companies')({
  validateSearch: companiesSearchSchema,
  loader: loadCompanies,
  pendingComponent: CompaniesPending,
  errorComponent: CompaniesError,
  component: CompaniesScreen,
})

function CompaniesScreen() {
  const { search, sector } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [term, setTerm] = useState(search ?? '')

  // Hand-rolled debounce, matching StockPricesScreen's and AllBrokersScreen's
  // own: 300ms after the last keystroke the term lands in the URL as
  // `search`.
  //
  // Guarded by comparing against the last value actually committed to the
  // URL, not a "have I run once" boolean — a boolean guard set on the first
  // effect run and checked on the next looks equivalent, but React 18's
  // <StrictMode> (src/main.tsx) double-invokes effects on mount: run one sets
  // the boolean and returns before scheduling anything, and run two, seeing
  // it already set, schedules the debounce and fires `navigate` anyway — the
  // exact mount-time call the guard exists to prevent, still happening,
  // just one extra tick later. Comparing values instead of counting runs
  // sidesteps that: `next` on mount is derived from `term`, which was itself
  // initialised from `search`, so it equals `lastCommittedSearch.current`
  // regardless of how many times StrictMode replays the effect, and the
  // guard skips every one of those replays. It only lets `navigate` through
  // once `term` has actually diverged from what the URL already holds.
  const lastCommittedSearch = useRef(search)
  useEffect(() => {
    const next = term.trim() || undefined
    if (next === lastCommittedSearch.current) return
    const id = setTimeout(() => {
      lastCommittedSearch.current = next
      void navigate({ search: (prev) => ({ ...prev, search: next }) })
    }, 300)
    return () => {
      clearTimeout(id)
    }
  }, [term, navigate])

  // Not a suspense read: the filters are search params, and a suspense query
  // whose key changed would put the page back on its pending skeleton. The
  // loader has cached the first page for the URL the page opened on; a new
  // search keeps the previous rows on screen until the next land.
  const query = useCompanyList({ search, sector })
  const rows = query.data?.pages.flatMap((p) => p.rows) ?? []
  /**
   * How many companies the filters actually match.
   *
   * Printable now, and only now: `companyService.list` walks `/market/movers/` to
   * exhaustion, so `total` counts the whole register rather than the 200-row
   * `/market/live/` window it used to count. The page had no count at all while
   * that was true, which meant the cap was invisible — a count sourced from a
   * ceiling is worse than no count, and a count sourced from the register is
   * better than either.
   */
  const total = query.data?.pages[0]?.total

  const loadMore = useCallback(() => {
    void query.fetchNextPage()
  }, [query])

  // A search that hits the paywall after the page has loaded; one on the way
  // in is the route's error component.
  if (query.isError && isPaywall(query.error)) {
    return (
      <ScreenLayout {...FRAME}>
        <EmptyState
          title="Subscription required"
          description="This data needs an active Guru plan."
        />
      </ScreenLayout>
    )
  }

  return (
    <ScreenLayout
      {...FRAME}
      header={
        <TitleRow
          title="Companies"
          meta={
            total === undefined ? undefined : (
              <span className="tnum">
                {String(total)} {total === 1 ? 'company' : 'companies'}
              </span>
            )
          }
          actions={
            <>
              <UpdatingNote when={query.isPlaceholderData} label="Updating list…" />
              <input
                type="search"
                value={term}
                placeholder="Search symbol or name"
                aria-label="Search companies"
                className="h-9 w-64 rounded-md border border-input bg-background px-3 text-body-l lg:text-body-m"
                onChange={(e) => {
                  setTerm(e.target.value)
                }}
              />
            </>
          }
        />
      }
    >
      <RefreshRegion updating={query.isPlaceholderData} className="flex flex-col gap-4">
        <CompanyTable
          rows={rows}
          isLoading={query.isLoading}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          onLoadMore={loadMore}
        />
      </RefreshRegion>
    </ScreenLayout>
  )
}
